import type { AgentChatIntent } from "@common/schemas/domains/agentChat/agent-chat.schema";
import type { LiveIntent, LiveToolRow } from "@api/agentChatStream.api";

const MAX_ARG_HINT_LENGTH = 40;

// Tool candidates above this router probability are surfaced together; below it
// they're treated as noise — except we always surface the single most-likely
// candidate so the step never falls back to the generic intent label when the
// router did return tools.
const RELEVANT_TOOL_PROBABILITY_THRESHOLD = 0.3;

// Shown when no tool candidate clears the threshold (e.g. a clarification or
// off-topic turn that won't call tools).
const INTENT_FALLBACK_LABELS: Record<AgentChatIntent, string> = {
    data_query: "Looking up your data",
    data_mutation: "Updating your books",
    workflow_explanation: "Researching the answer",
    clarification_only: "Asking a clarifying question",
    off_topic: "Thinking it through",
};

// The live "matching" step label: a progress message while intent detection
// runs (which can take a few seconds), then the relevant tool(s) once the
// router resolves.
export function formatMatchingLabel(intent: LiveIntent | null): string {
    if (!intent) {
        return "Matching available tools…";
    }
    const relevant = intent.toolCandidates
        .filter(
            (candidate) =>
                candidate.probability > RELEVANT_TOOL_PROBABILITY_THRESHOLD,
        )
        .map((candidate) => candidate.name);
    // Always surface at least the most-likely candidate when the router
    // returned any — a sub-threshold guess is more informative than the generic
    // intent fallback.
    const names =
        relevant.length > 0
            ? relevant
            : topCandidateName(intent.toolCandidates);
    if (names.length === 0) {
        return INTENT_FALLBACK_LABELS[intent.resolvedIntent];
    }
    const noun = names.length === 1 ? "tool" : "tools";
    return `Found relevant ${noun}: ${names.join(", ")}`;
}

// The single highest-probability candidate's name, or `[]` when there are none.
function topCandidateName(
    toolCandidates: LiveIntent["toolCandidates"],
): string[] {
    let top: LiveIntent["toolCandidates"][number] | null = null;
    for (const candidate of toolCandidates) {
        if (top === null || candidate.probability > top.probability) {
            top = candidate;
        }
    }
    return top ? [top.name] : [];
}

// Human-readable tool-call duration: ms under a second, otherwise seconds.
export function formatToolDuration(durationMs: number | null): string {
    if (durationMs === null) {
        return "";
    }
    if (durationMs < 1000) {
        return `${durationMs}ms`;
    }
    return `${(durationMs / 1000).toFixed(1)}s`;
}

// `{argHint} · {summary}` — argHint is the first short primitive argument,
// summary is the tool's result summary (or the web-search status). Either part
// is omitted when empty (e.g. a still-running row has no summary yet).
export function formatToolRowSubtitle(row: LiveToolRow): string {
    const argHint = firstPrimitiveArg(row.input);
    const summary = toolRowSummary(row);
    return [argHint, summary].filter((part) => part.length > 0).join(" · ");
}

function toolRowSummary(row: LiveToolRow): string {
    if (!row.entry) {
        return "";
    }
    if (row.entry.kind === "tool") {
        return row.entry.result.summary;
    }
    return `searched the web (${row.entry.status})`;
}

function firstPrimitiveArg(input: Record<string, unknown>): string {
    for (const value of Object.values(input)) {
        if (typeof value === "string" && value.length > 0) {
            return value.length > MAX_ARG_HINT_LENGTH
                ? `${value.slice(0, MAX_ARG_HINT_LENGTH)}…`
                : value;
        }
        if (typeof value === "number" || typeof value === "boolean") {
            return String(value);
        }
    }
    return "";
}
